import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { 
  Users, DollarSign, Package, TrendingUp, TrendingDown,
  Clock, ArrowRight, RefreshCw, Calendar, ShoppingBag, ChevronRight, Check
} from 'lucide-react';
import { formatPrice, formatDate } from '../../lib/utils';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const getRevenueChange = () => {
    if (!stats || !stats.last_month_revenue) return 0;
    if (stats.last_month_revenue === 0) return stats.this_month_revenue > 0 ? 100 : 0;
    return ((stats.this_month_revenue - stats.last_month_revenue) / stats.last_month_revenue * 100).toFixed(1);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-neutral-200 border-t-black rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const revenueChange = getRevenueChange();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Dashboard</h1>
            <p className="text-sm text-[#525252] mt-1">Welcome back! Here's your business overview.</p>
          </div>
          <button 
            onClick={loadStats}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Revenue Card */}
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#16A34A]" />
              </div>
              {revenueChange !== 0 && (
                <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                  revenueChange > 0 ? 'bg-[#DCFCE7] text-[#16A34A]' : 'bg-[#FEE2E2] text-[#DC2626]'
                }`}>
                  {revenueChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {revenueChange > 0 ? '+' : ''}{revenueChange}%
                </span>
              )}
            </div>
            <p className="text-2xl font-semibold text-[#16A34A] tracking-tight">{formatPrice(stats?.this_month_revenue || 0)}</p>
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mt-1">This Month Revenue</p>
          </div>

          {/* Clients Card */}
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <Users className="w-5 h-5 text-[#2563EB]" />
              </div>
              {stats?.new_clients_this_month > 0 && (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-[#DBEAFE] text-[#2563EB]">
                  +{stats.new_clients_this_month} new
                </span>
              )}
            </div>
            <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{stats?.total_clients || 0}</p>
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mt-1">Total Clients</p>
          </div>

          {/* Active Services Card */}
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#F3E8FF] flex items-center justify-center">
                <Package className="w-5 h-5 text-[#9333EA]" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{stats?.active_services || 0}</p>
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mt-1">Active Services</p>
          </div>

          {/* Pending Orders Card */}
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#D97706]" />
              </div>
              {stats?.pending_orders > 0 && (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-[#FEE2E2] text-[#DC2626]">
                  {stats.pending_orders} pending
                </span>
              )}
            </div>
            <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{stats?.pending_orders || 0}</p>
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mt-1">Pending Orders</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Revenue & Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Revenue Chart */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-base font-semibold text-[#0A0A0A]">Revenue Overview</h3>
                  <p className="text-sm text-[#525252]">Last 6 months performance</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#A3A3A3] uppercase">MRR</p>
                  <p className="text-lg font-semibold text-[#2563EB]">{formatPrice(stats?.mrr || 0)}</p>
                </div>
              </div>
              
              <div className="h-44 flex items-end gap-3">
                {stats?.revenue_by_month?.map((month, idx) => {
                  const maxRevenue = Math.max(...(stats?.revenue_by_month?.map(m => m.revenue) || [1]));
                  const height = maxRevenue > 0 ? (month.revenue / maxRevenue * 100) : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-2 group">
                      <div 
                        className="w-full bg-black rounded-lg transition-all hover:bg-[#262626] relative cursor-pointer"
                        style={{ height: `${Math.max(height, 8)}%`, minHeight: '8px' }}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white rounded-lg px-2 py-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {formatPrice(month.revenue)}
                        </div>
                      </div>
                      <span className="text-xs text-[#525252] font-medium">{month.month}</span>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#EBEBEB]">
                <div>
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-wide">Total Revenue</p>
                  <p className="text-xl font-semibold text-[#0A0A0A]">{formatPrice(stats?.total_revenue || 0)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-wide">Total Orders</p>
                  <p className="text-xl font-semibold text-[#0A0A0A]">{stats?.total_orders || 0}</p>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-[#EBEBEB]">
                <div>
                  <h3 className="text-base font-semibold text-[#0A0A0A]">Recent Activity</h3>
                  <p className="text-sm text-[#525252]">Latest orders and registrations</p>
                </div>
                <Link 
                  to="/admin/orders"
                  className="flex items-center gap-1 text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors"
                >
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="divide-y divide-[#EBEBEB]">
                {stats?.recent_orders?.slice(0, 5).map((order, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 hover:bg-[#FAFAFA] transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                        <ShoppingBag className="w-4 h-4 text-[#525252]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0A0A0A]">{order.client_name || 'Unknown'}</p>
                        <p className="text-xs text-[#A3A3A3]">{order.service_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#0A0A0A]">{formatPrice(order.price || 0)}</p>
                      <p className="text-xs text-[#A3A3A3]">{formatDate(order.created_at)}</p>
                    </div>
                  </div>
                ))}
                {(!stats?.recent_orders || stats.recent_orders.length === 0) && (
                  <div className="p-8 text-center">
                    <p className="text-sm text-[#A3A3A3]">No recent orders</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Quick Actions & Widgets */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-5">
              <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link 
                  to="/admin/clients"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#2563EB]" />
                  </div>
                  <span className="text-sm font-medium text-[#525252] group-hover:text-[#0A0A0A] flex-1">Manage Clients</span>
                  <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />
                </Link>
                <Link 
                  to="/admin/services"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#F3E8FF] flex items-center justify-center">
                    <Package className="w-4 h-4 text-[#9333EA]" />
                  </div>
                  <span className="text-sm font-medium text-[#525252] group-hover:text-[#0A0A0A] flex-1">Manage Services</span>
                  <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />
                </Link>
                <Link 
                  to="/admin/orders"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                    <ShoppingBag className="w-4 h-4 text-[#D97706]" />
                  </div>
                  <span className="text-sm font-medium text-[#525252] group-hover:text-[#0A0A0A] flex-1">View Orders</span>
                  <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />
                </Link>
              </div>
            </div>

            {/* Upcoming Renewals */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-[#EBEBEB]">
                <h3 className="text-base font-semibold text-[#0A0A0A]">Upcoming Renewals</h3>
                <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-[#D97706]" />
                </div>
              </div>
              <div className="divide-y divide-[#EBEBEB]">
                {stats?.upcoming_renewals?.slice(0, 4).map((renewal, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 hover:bg-[#FAFAFA] transition-colors">
                    <div>
                      <p className="text-sm font-medium text-[#0A0A0A]">{renewal.client_name}</p>
                      <p className="text-xs text-[#A3A3A3]">{renewal.service_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium text-[#D97706]">{formatDate(renewal.renewal_date)}</p>
                      <p className="text-xs text-[#A3A3A3]">{formatPrice(renewal.price)}</p>
                    </div>
                  </div>
                ))}
                {(!stats?.upcoming_renewals || stats.upcoming_renewals.length === 0) && (
                  <div className="p-6 text-center">
                    <p className="text-sm text-[#A3A3A3]">No upcoming renewals</p>
                  </div>
                )}
              </div>
            </div>

            {/* New Clients */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-[#EBEBEB]">
                <h3 className="text-base font-semibold text-[#0A0A0A]">New Clients</h3>
                <Link 
                  to="/admin/clients"
                  className="text-xs font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors"
                >
                  View All
                </Link>
              </div>
              <div className="divide-y divide-[#EBEBEB]">
                {stats?.recent_clients?.slice(0, 4).map((client, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 hover:bg-[#FAFAFA] transition-colors">
                    <div className="w-9 h-9 rounded-full bg-[#F5F5F5] flex items-center justify-center text-sm font-semibold text-[#525252]">
                      {client.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0A0A0A] truncate">{client.name}</p>
                      <p className="text-xs text-[#A3A3A3] truncate">{client.email}</p>
                    </div>
                  </div>
                ))}
                {(!stats?.recent_clients || stats.recent_clients.length === 0) && (
                  <div className="p-6 text-center">
                    <p className="text-sm text-[#A3A3A3]">No new clients</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
